import type { AxiosInstance } from "axios";
import { createAuthenticatedApiClient } from "@/shared/api/axios";
import { getAppSettings } from "@/shared/config/app-settings";

/**
 * Shape of the user-management detailed-user response (the fields we read).
 * Same source MerchantApp uses: OneID claims are surfaced by the API, not by
 * the reference access token this client receives.
 */
interface DetailedUserResponse {
  OneIdClaim?: {
    Perm?: string[];
  };
}

let userManagementClient: AxiosInstance | undefined;

function getUserManagementClient(baseUrl: string): AxiosInstance {
  userManagementClient ??= createAuthenticatedApiClient(baseUrl);
  return userManagementClient;
}

/**
 * Fetches the user's permission ids. Two supported sources per app config:
 * - JWT-token clients (e.g. WMS/ToolBob-style) carry `Perm` in the token -
 *   the auth store reads it there and never calls this.
 * - Reference-token clients (e.g. ShipBob.OneId.User.Clone) fetch from the
 *   user-management API. Requires `userManagementApiUrl` in appsettings AND
 *   the client_id present in the API's UserReadDataPermissionList allowlist.
 * With neither source configured, permissions resolve empty (deny-all).
 */
export async function fetchUserPermissions(): Promise<string[]> {
  const { userManagementApiUrl } = getAppSettings();
  if (!userManagementApiUrl) {
    console.warn(
      "[Auth] Token has no Perm claim and no userManagementApiUrl is configured, permissions unavailable (deny-all).",
    );
    return [];
  }

  const { data } = await getUserManagementClient(userManagementApiUrl).get<DetailedUserResponse>(
    "/api/userData/fetchDetailedUserData",
  );
  return data?.OneIdClaim?.Perm ?? [];
}
